import * as React from "react";
import type { Email } from "@src/types/api";
import type { OutlookMailSession } from "@src/load-agent/types/office";
import type { LoadApplicationWiring } from "@src/load-agent/LoadApplication";
import { OfficeJsHost } from "@src/load-agent/office/outlookHost";
import { LoadApplication } from "@src/load-agent/LoadApplication";
import { createDefaultPostBookWorkflowSideEffects } from "@src/load-agent/domain/postBookWorkflowSideEffects";
import {
  ShipmentRowActions,
  type ShipmentRowActionsApi,
} from "@src/load-agent/integrations/shipmentRowActions";

export interface TaskpaneServices {
  office: OutlookMailSession;
  loadApp: LoadApplicationWiring;
  shipmentActions: ShipmentRowActionsApi;
}

const TaskpaneServicesReactContext = React.createContext<TaskpaneServices | null>(null);

export function TaskpaneServicesProvider({
  children,
  services,
}: {
  children: React.ReactNode;
  services?: TaskpaneServices;
}) {
  const value = React.useMemo<TaskpaneServices>(() => {
    if (services) {
      return services;
    }
    const office = new OfficeJsHost();
    const loadApp = new LoadApplication();
    const shipmentActions: ShipmentRowActionsApi = {
      book: () =>
        Promise.reject(new Error("No email loaded — cannot book before email data is available")),
      select: () => {},
      open: () => {},
    };

    return {
      office,
      loadApp,
      shipmentActions,
    };
  }, [services]);

  return (
    <TaskpaneServicesReactContext.Provider value={value}>
      {children}
    </TaskpaneServicesReactContext.Provider>
  );
}
export function createShipmentActions(
  emailData: Email | null,
  deps: Pick<TaskpaneServices, "loadApp">,
  onSelectionChange?: () => void,
  onNavigate?: (mode: "FTL" | "LTL", shipmentId: string) => void,
  onRefreshEmailData?: () => Promise<unknown>
): ShipmentRowActionsApi {
  const loadApp = deps.loadApp;
  const fx = createDefaultPostBookWorkflowSideEffects(loadApp);

  return new ShipmentRowActions(
    emailData,
    { loadApp, fx },
    onSelectionChange,
    onNavigate,
    onRefreshEmailData
  );
}
export function useTaskpaneServices(): TaskpaneServices {
  const ctx = React.useContext(TaskpaneServicesReactContext);

  if (!ctx) {
    throw new Error("TaskpaneServicesProvider is missing above this component.");
  }
  return ctx;
}
